/**
 * MerchantRedirectService - Handles redirecting to merchant sites
 *
 * This service prepares and executes redirects to external merchant systems
 * like Alzura/Tyre24 after checkout completion.
 */

import axios from 'axios';

/**
 * Prepare merchant redirect to Alzura
 *
 * @param {string} locale - Locale code (de, es, tr, etc.)
 * @returns {Promise<Object>} Redirect data
 */
export async function prepareMerchantRedirect(locale = 'de') {
  try {
    const response = await axios.post('/api/checkout/prepare-merchant-redirect', {
      locale,
    });

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.error || 'Failed to prepare redirect');
  } catch (error) {
    console.error('Merchant redirect preparation failed:', error);
    throw error;
  }
}

/**
 * Execute redirect to merchant
 *
 * Handles both cookie-based and header-based redirects:
 * - Cookie-based: Sets cookies and submits form (traditional)
 * - Header-based: Uses fetch API with custom headers (modern)
 *
 * @param {Object} redirectData - Redirect data from API
 * @param {string} redirectData.redirect_url - Target URL
 * @param {string} redirectData.method - HTTP method (POST/GET)
 * @param {Object} redirectData.payload - Data to send
 * @param {Object} redirectData.cookies_to_transfer - Cookies to set
 * @param {Object} redirectData.headers_to_transfer - Headers to include
 */
export function executeMerchantRedirect(redirectData) {
  const { redirect_url, method, payload, cookies_to_transfer = {}, headers_to_transfer = {} } = redirectData;

  // Set cookies if any
  if (cookies_to_transfer && Object.keys(cookies_to_transfer).length > 0) {
    setCookies(cookies_to_transfer);
  }

  // Choose redirect method based on headers
  const hasHeaders = headers_to_transfer && Object.keys(headers_to_transfer).length > 0;

  if (hasHeaders) {
    // Modern API-based redirect with headers
    executeRedirectWithHeaders(redirect_url, method, payload, headers_to_transfer);
  } else {
    // Traditional form-based redirect (for cookies)
    executeRedirectWithForm(redirect_url, method, payload);
  }
}

/**
 * Set cookies in browser
 *
 * @param {Object} cookies - Key-value pairs of cookies
 */
function setCookies(cookies) {
  Object.entries(cookies).forEach(([name, value]) => {
    // Set cookie with proper attributes
    document.cookie = `${name}=${value}; path=/; SameSite=Lax`;
  });
}

/**
 * Execute redirect using form submission (for cookie-based auth)
 *
 * @param {string} url - Target URL
 * @param {string} method - HTTP method
 * @param {Object} payload - Data to send
 */
function executeRedirectWithForm(url, method, payload) {
  // Create hidden form
  const form = document.createElement('form');
  form.method = method;
  form.action = url;
  form.style.display = 'none';

  // Add payload as hidden fields
  addFormFields(form, payload);

  // Append to body and submit
  document.body.appendChild(form);
  form.submit();
}

/**
 * Add fields to form recursively
 *
 * @param {HTMLFormElement} form - Form element
 * @param {Object} data - Data to add
 * @param {string} prefix - Field name prefix for nested objects
 */
function addFormFields(form, data, prefix = '') {
  Object.entries(data).forEach(([key, value]) => {
    const fieldName = prefix ? `${prefix}[${key}]` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Nested object - recurse
      addFormFields(form, value, fieldName);
    } else if (Array.isArray(value)) {
      // Array - add each item
      value.forEach((item, index) => {
        if (typeof item === 'object') {
          addFormFields(form, item, `${fieldName}[${index}]`);
        } else {
          addHiddenInput(form, `${fieldName}[${index}]`, item);
        }
      });
    } else {
      // Primitive value
      addHiddenInput(form, fieldName, value);
    }
  });
}

/**
 * Add hidden input to form
 *
 * @param {HTMLFormElement} form - Form element
 * @param {string} name - Input name
 * @param {*} value - Input value
 */
function addHiddenInput(form, name, value) {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value !== undefined && value !== null ? String(value) : '';
  form.appendChild(input);
}

/**
 * Execute redirect using fetch API (for header-based auth)
 *
 * @param {string} url - Target URL
 * @param {string} method - HTTP method
 * @param {Object} payload - Data to send
 * @param {Object} headers - Custom headers
 */
async function executeRedirectWithHeaders(url, method, payload, headers) {
  try {
    const response = await fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        ...headers, // Include custom headers
      },
      body: JSON.stringify(payload),
      credentials: 'include', // Also send cookies
    });

    // Handle response - might redirect to another URL
    if (response.redirected) {
      window.location.href = response.url;
    } else if (response.ok) {
      const data = await response.json();
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      }
    } else {
      console.error('Redirect request failed:', response.status, response.statusText);
      throw new Error(`Redirect failed: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('Redirect with headers failed:', error);
    throw error;
  }
}

/**
 * Check if user has Alzura connection
 *
 * @returns {Promise<boolean>} Whether user has Alzura ID
 */
export async function checkAlzuraConnection() {
  try {
    const response = await axios.get('/api/merchant/check-connection');
    return response.data || { has_connection: false };
  } catch (error) {
    console.error('Failed to check Alzura connection:', error);
    return { has_connection: false };
  }
}
